import { Link } from 'react-router-dom';
import { useCommerceEnabled, useStorefrontContent } from '../lib/hooks';
import { isCommercePath } from '@mlm/shared';
import { storefrontAssetUrl } from '../../lib/api';

/** Internal hrefs (starting with "/") navigate via the router; anything else is
 * an external anchor. Keeps storefront nav SPA-fast without a full reload. */
function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  const cls = 'mb-[9px] block cursor-pointer text-[13px] text-[#666] hover:text-o';
  return href.startsWith('/') ? (
    <Link to={href} className={cls}>
      {children}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {children}
    </a>
  );
}

export function Footer() {
  const { footer, social } = useStorefrontContent().data;
  const socialLinks = social.filter((s) => s.url.trim());
  // On an information-only site the shop, sign-up and member portal routes
  // aren't mounted, so CMS links pointing at them would dead-end on the
  // catch-all redirect. Drop them here rather than rewriting stored content —
  // live tenants hold their own admin-edited copies of these columns.
  const commerce = useCommerceEnabled();
  const columns = commerce
    ? footer.columns
    : footer.columns
        .map((c) => ({ ...c, links: c.links.filter((l) => !isCommercePath(l.href)) }))
        .filter((c) => c.links.length > 0);
  return (
    <footer className="mt-9 border-t-4 border-o bg-white">
      <div className="mx-auto max-w-[1180px] px-4">
        <div className="grid gap-6 py-[34px] md:grid-cols-[1.6fr_repeat(4,1fr)]">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-o">{footer.brandName}</div>
            <p className="mt-3 max-w-[34ch] text-[13px] leading-[1.6] text-[#777]">
              {footer.brandBlurb}
            </p>
          </div>
          {columns.map((c) => (
            <div key={c.heading}>
              <h4 className="mb-3.5 text-xs uppercase tracking-[0.07em] text-[#888]">{c.heading}</h4>
              {c.links.map((l) => (
                <FooterLink key={l.label} href={l.href}>
                  {l.label}
                </FooterLink>
              ))}
            </div>
          ))}
          {socialLinks.length > 0 && (
            <div>
              <h4 className="mb-3.5 text-xs uppercase tracking-[0.07em] text-[#888]">Follow Us</h4>
              <div className="flex flex-col gap-2.5">
                {socialLinks.map((s) => (
                  <a
                    key={s.platform + s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[13px] text-[#666] hover:text-o"
                  >
                    {s.logoKey ? (
                      <img
                        src={storefrontAssetUrl(s.logoKey)}
                        alt=""
                        className="h-5 w-5 flex-none rounded object-contain"
                      />
                    ) : (
                      <span className="h-5 w-5 flex-none rounded bg-[#f0f0f0]" />
                    )}
                    {s.platform}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t border-[#ececec] py-[18px] text-xs text-[#999]">
          <span>{footer.copyrightText}</span>
          <span>{footer.permitText}</span>
        </div>
      </div>
    </footer>
  );
}
